/**
 * Um texto que existe para ser COPIADO: o endereço do link e o trecho de instalação.
 *
 * Três decisões que parecem detalhe e não são:
 *
 *  1. **A confirmação é visível e fica.** "Copiado" some depois de alguns segundos, mas
 *     enquanto está lá é texto, não só um ícone trocando de cor — e vai num `aria-live`,
 *     porque quem usa leitor de tela não tem como saber se o clique fez alguma coisa.
 *  2. **A falha tem plano B.** `navigator.clipboard` não existe em `http://` fora do
 *     localhost, e o navegador pode negar a permissão sem avisar. Nesses casos o texto é
 *     SELECIONADO na tela e a frase manda copiar à mão — o que não pode acontecer é o botão
 *     parecer ter funcionado quando não copiou nada, com o cliente colando o vazio no
 *     construtor da página dele.
 *  3. **O texto continua selecionável.** Bloco de código que não dá para selecionar obriga a
 *     confiar no botão; aqui os dois caminhos funcionam.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@mgdlead/ui';
import { Check, Copy } from 'lucide-react';

export interface CopyBlockProps {
  /** O que vai para a área de transferência. */
  value: string;
  /** Nome da coisa, para o `aria-label` e para a confirmação ("Endereço copiado"). */
  what: string;
  /** `pre` para o trecho de código (multilinha); `inline` para o endereço. */
  variant?: 'inline' | 'pre';
  /** Botão de largura cheia no celular (regra da casa para ação importante). */
  block?: boolean;
}

/** Quanto tempo a confirmação fica na tela. Curto demais some antes de ser lida. */
const CONFIRMACAO_MS = 3_000;

type Estado = 'parado' | 'copiado' | 'falhou';

export function CopyBlock({ value, what, variant = 'inline', block = false }: CopyBlockProps) {
  const [estado, setEstado] = useState<Estado>('parado');
  const textoRef = useRef<HTMLElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trocar de link com a confirmação na tela diria "copiado" sobre o texto errado.
  useEffect(() => {
    setEstado('parado');
  }, [value]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  const selecionar = useCallback(() => {
    const node = textoRef.current;
    if (node === null || typeof window.getSelection !== 'function') return;
    const selection = window.getSelection();
    if (selection === null) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  const copiar = useCallback(() => {
    const marcar = (proximo: Estado) => {
      setEstado(proximo);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setEstado('parado'), CONFIRMACAO_MS);
    };

    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
      selecionar();
      marcar('falhou');
      return;
    }

    void clipboard.writeText(value).then(
      () => marcar('copiado'),
      () => {
        selecionar();
        marcar('falhou');
      },
    );
  }, [selecionar, value]);

  const Texto = variant === 'pre' ? 'pre' : 'span';

  return (
    <div className="flex flex-col gap-2">
      <div
        className={
          variant === 'pre'
            ? 'overflow-x-auto rounded-md border border-border bg-muted/40 p-3'
            : 'overflow-x-auto rounded-md border border-border bg-muted/40 px-3 py-2'
        }
      >
        <Texto
          ref={textoRef as never}
          className={
            variant === 'pre'
              ? 'whitespace-pre font-mono text-2xs leading-relaxed text-foreground'
              : 'whitespace-nowrap font-mono text-xs text-foreground'
          }
        >
          {value}
        </Texto>
      </div>

      <div className="flex flex-col gap-1">
        <Button
          variant="secondary"
          size="sm"
          block={block}
          leadingIcon={estado === 'copiado' ? <Check /> : <Copy />}
          aria-label={`Copiar ${what.toLowerCase()}`}
          onClick={copiar}
          className={block ? undefined : 'max-md:w-full'}
        >
          {estado === 'copiado' ? 'Copiado' : `Copiar ${what.toLowerCase()}`}
        </Button>

        {/*
          `aria-live` num nó que existe SEMPRE: criar o nó junto com a mensagem faz muito
          leitor de tela não anunciar nada, porque a região entra na página já preenchida.
        */}
        <span aria-live="polite" className="min-h-4 text-2xs">
          {estado === 'copiado' ? (
            <span className="text-success">{what} copiado.</span>
          ) : estado === 'falhou' ? (
            <span className="text-warning">
              Seu navegador não deixou copiar automaticamente. O texto já está selecionado — copie
              com Ctrl+C (ou ⌘+C).
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}
